/**
 * Form Validation Hook
 * Provides validation utilities for forms
 */

import { useState } from 'react';

export const useFormValidation = () => {
  const [errors, setErrors] = useState({});

  const validateField = (name, value, rules = {}) => {
    const fieldErrors = [];

    if (rules.required && (!value || value.toString().trim() === '')) {
      fieldErrors.push(`${name} is required`);
    }

    if (rules.email && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        fieldErrors.push('Please enter a valid email address');
      }
    }

    if (rules.minLength && value && value.length < rules.minLength) {
      fieldErrors.push(`${name} must be at least ${rules.minLength} characters long`);
    }

    if (rules.match && value !== rules.match) {
      fieldErrors.push('Passwords do not match');
    }

    if (rules.passwordStrength && value) {
      const strength = calculatePasswordStrength(value);
      if (strength < (rules.passwordStrength || 50)) {
        fieldErrors.push('Password is too weak. Please choose a stronger password.');
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: fieldErrors
    }));

    return fieldErrors.length === 0;
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    return strength;
  };

  const clearErrors = () => {
    setErrors({});
  };

  const clearFieldError = (fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  const hasErrors = () => {
    return Object.keys(errors).length > 0;
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName];
  };

  return {
    errors,
    validateField,
    clearErrors,
    clearFieldError,
    hasErrors,
    getFieldError,
    calculatePasswordStrength
  };
};

export default useFormValidation;